/**
 * TimeInformationSection Component
 * Renders Zeitinformationen section in PDF
 */

import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { TimeInformationDatabaseData } from '@/types/common';
import { styles } from '../styles';
import { PDFTable, PDFTableRow } from './PDFTable';
import { CategoryDetailsTable } from './CategoryDetailsTable';
import { CategoryData } from '../types';

interface TimeInformationSectionProps {
  timeInfo: TimeInformationDatabaseData;
}

export const TimeInformationSection: React.FC<TimeInformationSectionProps> = ({ timeInfo }) => {
  const { t } = useTranslation('pdfReport');

  // Build one column per active category (toggle enabled AND category data present)
  const categoryColumns = [
    { active: timeInfo.jahr, data: timeInfo.category_data?.jahr, label: t('time.year') },
    { active: timeInfo.monat, data: timeInfo.category_data?.monat, label: t('time.month') },
    { active: timeInfo.woche, data: timeInfo.category_data?.woche, label: t('time.week') },
    { active: timeInfo.tag, data: timeInfo.category_data?.tag, label: t('time.day') },
    { active: timeInfo.feiertag, data: timeInfo.category_data?.feiertag, label: t('time.holiday') },
  ]
    .filter((c) => c.active && c.data)
    .map((c) => ({ label: c.label, data: c.data as CategoryData }));

  return (
    <View style={styles.section}>
      <Text style={styles.subtitle}>{t('time.section')}</Text>

      <PDFTable>
        <PDFTableRow label={t('time.year')} value={timeInfo.jahr} formatBoolean isEven />
        <PDFTableRow label={t('time.month')} value={timeInfo.monat} formatBoolean isEven={false} />
        <PDFTableRow label={t('time.week')} value={timeInfo.woche} formatBoolean isEven />
        <PDFTableRow label={t('time.day')} value={timeInfo.tag} formatBoolean isEven={false} />
        <PDFTableRow label={t('time.holiday')} value={timeInfo.feiertag} formatBoolean isEven />
        <PDFTableRow label={t('time.timezone')} value={timeInfo.zeitzone} isEven={false} isLast />
      </PDFTable>

      {/* Category Data Details - single grid, one column per active category */}
      {categoryColumns.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.subtitle}>{t('time.detailedCategories')}</Text>
          <CategoryDetailsTable categories={categoryColumns} />
        </View>
      )}
    </View>
  );
};
